<script setup>
import i18n from '@/plugins/i18n'
import { computed, ref } from 'vue'
import moment from 'moment'

import router, { menuRoutesEnum } from '@/router'
import { useRoute } from 'vue-router'

import { useDefaultKeyHandler as useDefaultGridKeyHandler } from '@/composables/data-grid'
import { useInitPortalStore } from '@/composables/use-init-portal-store'
import { usePortalStore } from '@/stores/portal.store.js'
import { useWebSiteStore } from '@/stores/web-site.store.js'

import AvatarTemplate from '@/components/shared/AvatarTemplate.vue'
import BreadCrumb from '@/components/shared/BreadCrumb.vue'
import LanguageSelector from '@/components/shared/LanguageSelector.vue'
import NotificationIcon from '@/components/shared/NotificationIcon.vue'
import SideBar from '@/components/shared/SideBar.vue'
import LetterOfAppointmentDialog from '@/components/shared/LetterOfAppointmentDialog.vue'

const t = i18n.global.t
useDefaultGridKeyHandler()

const { tryInitPortalStoreAsync } = useInitPortalStore(true)
await tryInitPortalStoreAsync()

const portalStore = usePortalStore()
const webSiteStore = useWebSiteStore()

const drawer = ref(false)
const route = useRoute()

const userDataFilter = computed(() => webSiteStore.userDataFilter)

const onUpdateFilterUserData = (value) => {
  webSiteStore.setUserDataFilter(value)
}

const userName = computed(() => {
  const userId = webSiteStore.user?.id
  const fullData = webSiteStore.userDetails?.find(u => u.user_id === userId)

  if (fullData && fullData.name && fullData.name_ext) {
    return `${fullData.name} ${fullData.name_ext}`
  }

  return webSiteStore.user?.name || webSiteStore.user?.username || ''
})

const logoSrc = webSiteStore.getLogo(true)

const onLogoutAsync = async() => {
  await webSiteStore.logoutAsync(webSiteStore.logoutActionEnum.LOGOUT)
}

const maintenanceData = computed(() => {
  const data = portalStore?.settings[0]?.data?.maintenance
  if (data) {
    const mStart = moment.utc(`${data.date} ${data.start}`, 'YYYY-MM-DD HH:mm', true).local().locale(portalStore.selectedLanguage)
    const mEnd = moment.utc(`${data.date} ${data.end}`, 'YYYY-MM-DD HH:mm', true).local().locale(portalStore.selectedLanguage)
    return {
      date: mStart.format('L'),
      start: mStart.format('LT'),
      end: mEnd.format('LT')
    }
  }
  return null
})

const showMaintenanceBanner = computed(() => {
  const raw = portalStore?.settings[0]?.data?.maintenance
  if (!raw) {
    return false
  }
  const mEndUtc = moment.utc(`${raw.date} ${raw.end}`, 'YYYY-MM-DD HH:mm', true).locale(portalStore.selectedLanguage)
  return moment.utc().isSameOrBefore(mEndUtc)
})

const layoutClass = computed(() => route.meta.layoutClass)

</script>
<template>
  <q-layout
    view="hHh Lpr lFf"
  >
    <q-header
      elevated
      class="header"
    >
      <q-toolbar
        class="text-white toolbar-background"
        elevated
      >
        <q-toolbar-title class="title">
          <div class="row">
            <img
              :src="logoSrc"
              alt="Logo"
              class="logo clickable"
              @click="router.push(menuRoutesEnum.HOME)"
            >
          </div>
        </q-toolbar-title>
        <div
          v-if="showMaintenanceBanner"
          class="absolute-center"
        >
          <q-banner
            inline-actions
            rounded
            class="bg-orange text-white mainenance-banner"
          >
            <q-icon
              name="mdi-alert"
              class="q-mr-xs"
            />
            {{ $t('warning.maintenance', maintenanceData) }}
          </q-banner>
        </div>
        <q-btn
          flat
          icon="mdi-apps"
          role="button"
          round
          class="q-mx-md"
          @click="async() => await webSiteStore.logoutAsync(false)"
        />
        <language-selector />
        <notification-icon />
        <q-separator
          dark
          vertical
          inset
          class="q-mx-md"
        />
        <q-btn
          round
          flat
          icon="mdi-dots-vertical"
          role="button"
          aria-label="Settings"
        >
          <q-menu
            anchor="bottom right"
            self="top right"
          >
            <div class="row no-wrap q-pa-md">
              <div class="column">
                <div class="text-h6 q-mb-md">
                  {{ $t('common.settings') }}
                </div>
                <q-toggle
                  :model-value="userDataFilter"
                  :label="t('common.userDataFilter')"
                  @update:model-value="onUpdateFilterUserData"
                />
              </div>

              <q-separator
                vertical
                inset
                class="q-mx-lg"
              />

              <div class="column items-center">
                <avatar-template
                  :current-user="true"
                />

                <div
                  class="text-subtitle1 q-mt-md q-mb-xs"
                >
                  {{ userName }}
                </div>

                <q-btn
                  v-close-popup
                  color="primary"
                  :label="$t('common.logout')"
                  size="sm"
                  @click="onLogoutAsync"
                />
              </div>
            </div>
            <q-list class="menu-list">
              <q-separator spaced />
              <q-item
                v-ripple
                tag="label"
              >
                <q-item-section>
                  <q-item-label>{{ $t('common.version') }}</q-item-label>
                </q-item-section>
                <q-item-section side>
                  <q-item-label>{{ [portalStore.version?.runtime_version, portalStore.environment].filter(Boolean).join('_') }} ({{ portalStore.version?.db_version }})</q-item-label>
                </q-item-section>
              </q-item>
            </q-list>
          </q-menu>
        </q-btn>
      </q-toolbar>
    </q-header>

    <SideBar
      v-model="drawer"
    />

    <q-page-container>
      <q-page
        :class="layoutClass"
        padding
      >
        <letter-of-appointment-dialog />
        <div
          class="breadcrumb"
          q-pt-xl
        >
          <bread-crumb />
        </div>
        <div>
          <router-view v-slot="{ Component }">
            <component
              :is="Component"
              :key="portalStore.reloadKey"
            />
          </router-view>
        </div>
      </q-page>
    </q-page-container>
  </q-layout>
</template>

<style scoped lang="scss">
@import '@/styles/quasar.variables.scss';

.header {
  z-index: 1000;
}
.menu-list {
  min-width: 20rem;
}
.logo {
  display: flex;
  align-items: center;
  height: 2rem;
}
.toolbar-background {
  background: linear-gradient(138deg, #fff, darken($primary, 10%));
}
.title {
  align-content: center;
}
.breadcrumb {
  font-size: small;
  display: flex;
  align-items: center;
  position: absolute;
  z-index: 2;
  margin-top: .8rem;
}
.layout-grouping-grid .breadcrumb {
  position: relative;
}
.clickable {
  cursor: pointer;
}
.mainenance-banner {
  min-height: auto;
}
</style>
